import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';

import {
    documentApi,
    useCreateDocumentsMutation,
    useDeleteDocumentMutation,
    useGetDocumentsQuery,
    useReplaceDocumentMutation,
} from 'api/main';
import { docTypes } from 'utils/constants';
import { checkImageFile, validateFileSize } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { Box, Typography } from '@mui/material';
import { ActionButton, CancelButton, FileErrorMessage, FileInputButton, Loader } from 'components';

const containerWidth = { lg: '50%', md: '100%', xl: '35%' };

const UpcomingProducts = () => {
    const dispatch = useDispatch();

    // get files of type
    const { data: documents = [], isFetching: documentsLoader } = useGetDocumentsQuery({
        type: docTypes.UPCOMING_PRODUCTS,
    });
    const [createDocuments, { isLoading: createLoader, isSuccess: createSuccess }] =
        useCreateDocumentsMutation();
    const [replaceDocument, { isLoading: replaceLoader, isSuccess: replaceSuccess }] =
        useReplaceDocumentMutation();
    const [deleteDocument, { isLoading: deleteLoader, isSuccess: deleteSuccess }] =
        useDeleteDocumentMutation();

    const [file, setFile] = useState(null);
    const [fileSizeError, setFileSizeError] = useState(false);
    const [fileTypeError, setFileTypeError] = useState(false);
    const [downloadingPreview, setDownloadingPreview] = useState(false);
    const [src, setSrc] = useState();

    // update preview of the existing file
    const preview = useMemo(() => (file ? URL.createObjectURL(file) : src), [file, src]);

    const handleChange = useCallback(e => {
        const file = e.target?.files[0];

        // validate file size
        if (validateFileSize(file)) {
            setFileSizeError(false);
        } else {
            setFileSizeError(true);
        }

        checkImageFile(
            file,
            () => {
                setFileTypeError(false);
                setFile(e.target.files[0]);
            },
            () => setFileTypeError(true)
        );
    }, []);

    const goBack = useCallback(() => {
        dispatch(push('/fund-management'));
    }, [dispatch]);

    const handleSubmit = useCallback(() => {
        // upload or replace document
        // if there's already a document uploaded
        if (documents?.[0]) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', docTypes.UPCOMING_PRODUCTS);
            replaceDocument({ formData, id: documents[0].id });
        } else {
            // this would only happen once on the first upload
            const formData = new FormData();
            formData.append('files[]', file);
            formData.append('type', docTypes.UPCOMING_PRODUCTS);
            createDocuments({ formData });
        }
    }, [createDocuments, documents, file, replaceDocument]);

    const handleDelete = useCallback(() => {
        if (documents?.[0]?.id) {
            deleteDocument(documents?.[0]?.id);
        }
    }, [deleteDocument, documents]);

    useEffect(() => {
        let mounted = true;
        let id = documents?.[0]?.id;
        if (id && !src) {
            // get image to be displayed
            setDownloadingPreview(true);
            documentApi
                .downloadDocumentById(id)
                .then(response => {
                    const bytes = new Uint8Array(response.data);
                    const blob = new Blob([bytes], { type: 'image/png' });
                    const url = window.URL.createObjectURL(blob);
                    if (url && mounted) {
                        setSrc(url);
                    }
                    if (mounted) {
                        setDownloadingPreview(false);
                    }
                })
                .catch(() => {
                    if (mounted) {
                        setDownloadingPreview(false);
                    }
                });
        }

        return () => {
            mounted = false;
        };
    }, [documents, src]);

    useEffect(() => {
        if (createSuccess || replaceSuccess || deleteSuccess) {
            setDownloadingPreview(false);
            setFile(null);
            setSrc();
        }
    }, [createSuccess, replaceSuccess, deleteSuccess]);

    return (
        <Box flex="1 1">
            <Typography variant="h5" align="left" gutterBottom>
                {texts.UPCOMING_PRODUCTS}
            </Typography>
            <Loader loading={documentsLoader}>
                <Box
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                    pt={file || src ? 10 : 20}
                    minHeight={400}
                    gap={10}
                    justifyContent={file || src ? 'space-around' : 'flex-start'}
                >
                    <Box
                        sx={{ width: containerWidth }}
                        display="flex"
                        justifyContent="space-around"
                        flexDirection="column"
                        alignItems="center"
                    >
                        {!preview ? (
                            <Loader loading={downloadingPreview} minHeight={200}>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    {texts.NO_FILE_UPLOADED}
                                </Typography>
                            </Loader>
                        ) : (
                            <Box
                                component="img"
                                alt="preview"
                                src={preview}
                                width="400px"
                                height="auto"
                                mb={3}
                                border="2px solid lightgrey"
                            />
                        )}
                        <FileInputButton
                            id="upcoming-product"
                            noMargin
                            showIcon
                            sx={{ flex: '1 1 45%' }}
                            onFileChange={handleChange}
                            label={texts.UPLOAD_FILE_BUTTON}
                            acceptedFileTypes={'.jpeg, .jpg, .png, .gif'}
                        />
                        {fileTypeError && <FileErrorMessage useMargin={false} variant="type" />}
                        {fileSizeError && <FileErrorMessage useMargin={false} variant="size" />}
                        {documents[0] && !file && (
                            <ActionButton
                                variant="contained"
                                sx={theme => ({
                                    backgroundColor: theme.palette.grey[500],
                                    marginY: 2,
                                    '&:hover': {
                                        backgroundColor: theme.palette.grey[600],
                                    },
                                })}
                                onClick={handleDelete}
                                loading={deleteLoader}
                            >
                                {texts.SUPPRIMER}
                            </ActionButton>
                        )}
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
                        <CancelButton disabled={createLoader || replaceLoader} onClick={goBack}>
                            {texts.CANCEL}
                        </CancelButton>
                        <ActionButton
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={createLoader || replaceLoader || !file}
                            loading={createLoader || replaceLoader}
                        >
                            {texts.SAVE}
                        </ActionButton>
                    </Box>
                </Box>
            </Loader>
        </Box>
    );
};

export default UpcomingProducts;
